import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Cloud, Download, Eye, Loader2, Lock, Save, Send, TestTube, ClipboardCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useFicStatus } from "@/context/FicStatusContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import SdiErrorDialog from "@/components/SdiErrorDialog";

export default function ActionsFooter({
  invoice,
  saving,
  lockState,
  onCancel,
    onDownloadXml,
  onOpenXml,
  onOpenFic,
  onSave,
  onSent,
  onDelete,
  xmlJustUpdated = false,
  dirty = false,
  lastSavedAt = null,
  autoSaveError = false,
}) {
  const { ready: ficReady } = useFicStatus();
  const [sdiConfig, setSdiConfig] = useState(null);
  const [showSdiConfirm, setShowSdiConfirm] = useState(false);
  const [showManualConfirm, setShowManualConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [markingManual, setMarkingManual] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sdiError, setSdiError] = useState(null);

  useEffect(() => {
    api.get("/sdi/config").then((r) => setSdiConfig(r.data)).catch(() => {});
  }, []);

  const canSendSdi = lockState ? lockState.can_send_sdi : true;
  const canSendFic = lockState ? lockState.can_send_fic : true;
  const isEditable = lockState ? lockState.is_editable : true;
  const alreadyManual = !!lockState?.manual_transmission;
  // GATE PRIMO SAVE (Feb 29, 2026 v16 — richiesta cliente):
  // Al primo caricamento/ricezione della fattura, l'utente deve VEDERE solo
  // "Salva". Le azioni di invio (SDI/FIC/Manuale) e visualizzazione XML
  // compaiono SOLO dopo il primo salvataggio, che rigenera l'XML sul backend
  // (proxy: `invoice.xml_path` presente = save già effettuato almeno una volta).
  const hasBeenSaved = !!invoice?.xml_path;
  const showSdiButton = hasBeenSaved && !ficReady && sdiConfig?.configured && canSendSdi;
  const showFicButton = hasBeenSaved && ficReady && canSendFic;
  const showSimulate = sdiConfig?.mode === "sandbox" && lockState?.sdi_sent;
  // "Trasmissione manuale" visibile solo dopo il primo save + finché la
  // fattura è editabile e non ancora marcata manuale.
  const showManualButton = hasBeenSaved && isEditable && !alreadyManual;
  // "Elimina fattura" (v17, richiesta cliente Feb 29, 2026): solo se la
  // fattura NON è mai stata trasmessa in nessun canale (SDI/FIC/manuale).
  // Anche una fattura scartata (NS/fic_rejected) NON è più cancellabile per
  // obbligo di audit trail. Il backend rifiuta con 409 in ogni caso, il
  // frontend nasconde il bottone per pulizia UX.
  const neverTransmitted = !lockState?.sdi_sent
    && !lockState?.fic_sent
    && !lockState?.manual_transmission;
  const showDeleteButton = !!onDelete && neverTransmitted;

  /**
   * Salva + rigenera XML → poi esegui `action`. Le azioni finali (SDI, FIC,
   * manuale) devono partire da un XML sempre aggiornato ai dati correnti,
   * senza costringere l'utente a un click intermedio su "Salva".
   */
  const saveThenSend = async (action) => {
    try {
      const saved = await onSave?.({ silent: true });
      if (saved && !saved.xml_path) {
        // Il backend è best-effort sulla generazione XML: se il profilo azienda
        // è incompleto, il save procede senza XML. Fermiamo qui con un dialog
        // parlante invece che lasciar fallire la POST /sdi/send.
        throw new Error(
          "Non è stato possibile aggiornare l'XML prima dell'invio. Verifica " +
            "che il profilo azienda sia completo (Partita IVA, indirizzo).",
        );
      }
      return await action();
    } catch (err) {
      const isSaveGate = err?.message?.startsWith("Non è stato possibile aggiornare");
      throw isSaveGate ? err : err;
    }
  };

  const sendSdi = async () => {
    setSending(true);
    try {
      await saveThenSend(() => api.post(`/sdi/send/${invoice.id}`));
      setShowSdiConfirm(false);
      if (typeof onSent === "function") await onSent();
      toast.success(
        `Fattura ${invoice.autofattura_number || ""} trasmessa al SDI.`.trim(),
        { description: "Riceverai una notifica appena il SDI risponderà con l'esito." },
      );
    } catch (e) {
      setShowSdiConfirm(false);
      setSdiError({
        title: "Trasmissione al SDI non riuscita",
        message:
          e?.response?.data?.detail ||
          e?.message ||
          "Non è stato possibile completare la trasmissione. Riprova tra qualche minuto.",
      });
    } finally {
      setSending(false);
    }
  };

  const markManualSent = async () => {
    setMarkingManual(true);
    try {
      await saveThenSend(() => api.post(`/invoices/${invoice.id}/mark-manual-sent`));
      setShowManualConfirm(false);
      if (typeof onSent === "function") await onSent();
      toast.success("Fattura segnata come trasmessa manualmente");
    } catch (e) {
      setShowManualConfirm(false);
      setSdiError({
        title: "Operazione non riuscita",
        message:
          e?.response?.data?.detail ||
          e?.message ||
          "Non è stato possibile marcare la fattura come trasmessa.",
      });
    } finally {
      setMarkingManual(false);
    }
  };

  const simulate = async (code) => {
    const label = { RC: "Consegnata", NS: "Scartata", MC: "Mancata consegna" }[code] || code;
    if (!window.confirm(`Simulare esito SDI "${label}" (${code})?\nDisponibile SOLO in sandbox.`))
      return;
    try {
      const { data } = await api.post(`/sdi/simulate/${invoice.id}`, { code });
      toast.success(`Esito ${code} simulato`, { description: `Stato: ${data.sdi_status || "in transito"}` });
      if (typeof onSent === "function") await onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulazione non riuscita");
    }
  };

  const isResendSdi = lockState?.sdi_rejected;
  const sdiEnvLabel = sdiConfig?.mode === "production" ? "produzione" : "sandbox";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9] p-5 sm:p-6 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-4">
        <button onClick={onCancel} data-testid="cancel-btn" className="text-sm text-zinc-600 hover:text-zinc-900">
          Annulla
        </button>
        <Link
          to="/app/invoices"
          data-testid="back-list-link"
          className="text-sm text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Torna all&apos;elenco
        </Link>
        {lockState && !isEditable && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#4A4A4A] bg-[#F5F2EA] border border-[#E8E4D9] px-2 py-1 rounded-full"
            data-testid="locked-pill"
          >
            <Lock className="w-3 h-3" />
            {alreadyManual ? "Trasmessa manualmente" : "Bloccata"}
          </span>
        )}
        {dirty && isEditable && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full animate-in fade-in slide-in-from-left-2 duration-200 ${
              autoSaveError
                ? "text-red-800 bg-red-50 border border-red-300"
                : saving
                  ? "text-amber-900 bg-amber-100 border border-amber-400"
                  : "text-amber-800 bg-amber-50 border border-amber-300"
            }`}
            data-testid="dirty-pill"
            title={
              autoSaveError
                ? "Il salvataggio automatico è fallito. Clicca Salva modifiche per riprovare."
                : saving
                  ? "Salvataggio in corso…"
                  : "Modifiche in attesa · salvataggio automatico tra 3s"
            }
          >
            {saving ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <span className={`w-1.5 h-1.5 rounded-full ${autoSaveError ? "bg-red-500" : "bg-amber-500 animate-pulse"}`} />
            )}
            {autoSaveError
              ? "Autosave fallito · salva a mano"
              : saving
                ? "Salvataggio…"
                : "Modifiche · autosave 3s"}
          </span>
        )}
        {!dirty && lastSavedAt && isEditable && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full animate-in fade-in duration-300"
            data-testid="saved-pill"
            title={`Ultimo salvataggio: ${lastSavedAt.toLocaleString("it-IT")}`}
          >
            <CheckCircle2 className="w-3 h-3" />
            Salvato · {lastSavedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {invoice.xml_path && (
          <div className="inline-flex items-center gap-2">
            <button
              onClick={onOpenXml}
              disabled={dirty}
              data-testid="view-xml-btn"
              title={dirty ? "Salva prima le modifiche per aggiornare l'XML" : "Apri l'XML aggiornato"}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Eye className="w-4 h-4" /> Visualizza XML
            </button>
            {xmlJustUpdated && !dirty && (
              <span
                data-testid="xml-updated-pill"
                className="animate-in fade-in slide-in-from-left-2 duration-300 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-wider shadow-sm"
                title="L'XML è stato appena rigenerato con i dati aggiornati"
              >
                <CheckCircle2 className="w-3 h-3" /> XML aggiornato ora
              </span>
            )}
          </div>
        )}
        {lockState?.sdi_status === "RC" && (
          <button
            onClick={async () => {
              try {
                const res = await api.get(`/invoices/${invoice.id}/sdi/receipt`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `ricevuta_SDI_${invoice.autofattura_number || invoice.id}.xml`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Ricevuta non disponibile");
              }
            }}
            data-testid="download-sdi-receipt-btn"
            title="Scarica il documento consegnato al SDI"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-emerald-600 text-emerald-700 bg-white text-sm font-medium hover:bg-emerald-50 transition-colors"
          >
            <Download className="w-4 h-4" /> Ricevuta SDI
          </button>
        )}
        {showManualButton && (
          <button
            onClick={() => setShowManualConfirm(true)}
            disabled={sending || markingManual || saving || dirty}
            data-testid="mark-manual-btn"
            title={dirty
              ? "Salva prima le modifiche per abilitare l'invio manuale"
              : "Salva le modifiche, rigenera l'XML e segna come trasmessa manualmente"}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-[#E8E4D9] text-[#0A0A0A] bg-white text-sm font-medium hover:bg-[#F5F2EA] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" /> Trasmissione manuale
          </button>
        )}
        {showSdiButton && (
          <button
            onClick={() => setShowSdiConfirm(true)}
            disabled={sending || markingManual || saving || dirty}
            data-testid="sdi-send-btn"
            title={dirty
              ? "Salva prima le modifiche per abilitare l'invio SDI"
              : "Salva le modifiche, rigenera l'XML e trasmetti allo SDI"}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
            {isResendSdi ? "Re-invia allo SDI" : "Invio allo SDI"}
          </button>
        )}
        {showFicButton && (
          <button
            onClick={onOpenFic}
            disabled={sending || markingManual || saving || dirty}
            data-testid="fic-send-open-btn"
            title={dirty
              ? "Salva prima le modifiche per abilitare l'invio a Fatture in Cloud"
              : "Salva le modifiche, rigenera l'XML e invia a Fatture in Cloud"}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-[#1D4ED8] text-white text-sm font-medium hover:bg-[#1E3A8A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Cloud className="w-4 h-4" />
            {lockState?.fic_rejected ? "Re-invia a Fatture in Cloud" : "Invia a Fatture in Cloud"}
          </button>
        )}
        {isEditable && (
          <button
            onClick={() => onSave?.()}
            disabled={saving}
            data-testid="save-invoice-btn"
            title={dirty ? "Modifiche non salvate — clicca per sincronizzarle" : "Salva la fattura"}
            className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-white text-sm font-bold uppercase tracking-wide disabled:opacity-70 transition-all ${
              dirty
                ? "bg-amber-600 hover:bg-amber-700 ring-2 ring-amber-300 shadow-md shadow-amber-500/20 animate-pulse"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {dirty ? "Salva modifiche *" : "Salva modifiche"}
          </button>
        )}
        {showDeleteButton && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving || deleting || sending || markingManual}
            data-testid="delete-invoice-btn"
            title="Cancella definitivamente questa fattura (non ancora trasmessa)"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-red-300 text-red-700 bg-white text-sm font-medium hover:bg-red-50 hover:border-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Elimina
          </button>
        )}
        {showSimulate && (
          <div
            className="inline-flex items-center rounded-md border border-dashed border-amber-400 bg-amber-50 pl-2 pr-1 py-1 gap-1"
            data-testid="sdi-simulate-group"
            title="Sandbox OpenAPI: forza un esito SDI per testare il ciclo di vita"
          >
            <TestTube className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mr-1">Sandbox</span>
            <button onClick={() => simulate("RC")} data-testid="sdi-simulate-rc" className="text-[11px] font-bold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">RC</button>
            <button onClick={() => simulate("NS")} data-testid="sdi-simulate-ns" className="text-[11px] font-bold px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">NS</button>
            <button onClick={() => simulate("MC")} data-testid="sdi-simulate-mc" className="text-[11px] font-bold px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700">MC</button>
          </div>
        )}
      </div>

      {/* Confirm dialog: invio SDI reale */}
      <ConfirmDialog
        open={showSdiConfirm}
        onOpenChange={setShowSdiConfirm}
        onConfirm={sendSdi}
        loading={sending}
        icon={Send}
        tone="primary"
        title={isResendSdi ? "Re-inviare la fattura allo SDI?" : "Inviare la fattura allo SDI?"}
        description={
          <>
            Stai per <strong>salvare le modifiche</strong>, aggiornare l&apos;XML e trasmettere{" "}
            <strong>{invoice.autofattura_number || "questa fattura"}</strong>{" "}
            al Sistema di Interscambio dell&apos;Agenzia delle Entrate ({sdiEnvLabel}).<br />
            Una volta trasmessa, il documento non potrà più essere modificato salvo scarto SDI.
          </>
        }
        confirmLabel={isResendSdi ? "Sì, re-invia allo SDI" : "Sì, invia allo SDI"}
        testid="sdi-confirm-dialog"
      />

      {/* Confirm dialog: trasmissione manuale */}
      <ConfirmDialog
        open={showManualConfirm}
        onOpenChange={setShowManualConfirm}
        onConfirm={markManualSent}
        loading={markingManual}
        icon={ClipboardCheck}
        tone="ink"
        title="Segnare come trasmessa manualmente?"
        description={
          <>
            Salviamo le tue modifiche, aggiorniamo l&apos;XML e blocchiamo la fattura{" "}
            <strong>{invoice.autofattura_number || "corrente"}</strong>{" "}
            come <em>già trasmessa</em> (portale Agenzia delle Entrate, commercialista, PEC…).<br />
            La fattura verrà bloccata come immutabile: potrai comunque riscaricare l&apos;XML.
          </>
        }
        confirmLabel="Sì, segna come trasmessa"
        testid="manual-confirm-dialog"
      />

      {/* Error dialog: SDI transmission failure */}
      <SdiErrorDialog
        open={!!sdiError}
        onOpenChange={(v) => { if (!v) setSdiError(null); }}
        title={sdiError?.title}
        message={sdiError?.message}
        invoiceRef={invoice.autofattura_number || undefined}
      />

      {/* Confirm dialog: cancellazione fattura (mai trasmessa) */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await onDelete?.();
            setShowDeleteConfirm(false);
          } finally {
            setDeleting(false);
          }
        }}
        loading={deleting}
        icon={Trash2}
        tone="danger"
        title="Eliminare definitivamente questa fattura?"
        description={
          <>
            Stai per cancellare <strong>{invoice.autofattura_number || invoice.file_name || "questa fattura"}</strong> dal tuo archivio.
            L&apos;operazione è <strong>irreversibile</strong>.<br /><br />
            <span className="text-xs text-zinc-600">
              • Se era stato assegnato un numero progressivo, verrà rilasciato per la prossima fattura (nessun buco fiscale).<br />
              • Il credito già consumato per l&apos;estrazione AI <strong>NON verrà rimborsato</strong> — il servizio è stato erogato.
            </span>
          </>
        }
        confirmLabel="Sì, elimina la fattura"
        testid="delete-confirm-dialog"
      />
    </div>
  );
}
